import { AnimatePresence, motion } from "motion/react";
import {
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { computePopoverPosition } from "@/shared/ui/overlay/anchoredPopoverPosition";
import { getDialogAccessibilityProps } from "@/shared/ui/overlay/dialogAccessibility";
import { useDialogFocusTrap } from "@/shared/ui/overlay/dialogFocus";
import { ModalContext } from "@/shared/ui/overlay/modal-context";
import { stopOverlayWheelPropagation } from "@/shared/ui/overlay/overlayWheel";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  ariaLabel: string;
  children: ReactNode;
}

/** PCで起点要素の近傍に表示する、フォーカストラップ付きの小型ポップオーバー。 */
export const AnchoredActionPopover: React.FunctionComponent<Props> = ({
  isOpen,
  onClose,
  anchorRef,
  ariaLabel,
  children,
}: Props) => {
  const dialogRef = useDialogFocusTrap<HTMLDivElement>(isOpen);
  const { registerModal, unregisterModal } = useContext(ModalContext);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [depth, setDepth] = useState<number | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });

  useEffect(() => {
    if (!isOpen) return;
    const registration = registerModal(() => onCloseRef.current(), {
      lockBodyScroll: false,
    });
    let disposed = false;
    queueMicrotask(() => {
      if (!disposed) setDepth(registration.depth);
    });
    return (): void => {
      disposed = true;
      unregisterModal(registration.id);
      queueMicrotask(() => setDepth(null));
    };
  }, [isOpen, registerModal, unregisterModal]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    let frame: number | null = null;

    const updatePosition = (): void => {
      const anchorRect = anchorRef.current?.getBoundingClientRect();
      if (!anchorRect) return;
      const popoverRect = dialogRef.current?.getBoundingClientRect();
      const next = computePopoverPosition(
        anchorRect,
        { width: popoverRect?.width ?? 0, height: popoverRect?.height ?? 0 },
        { width: window.innerWidth, height: window.innerHeight },
      );
      setPosition((previous) =>
        previous.left === next.left && previous.top === next.top
          ? previous
          : next,
      );
    };

    const scheduleUpdate = (): void => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };

    updatePosition();
    window.addEventListener("scroll", scheduleUpdate, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (anchorRef.current) resizeObserver.observe(anchorRef.current);
    if (dialogRef.current) resizeObserver.observe(dialogRef.current);

    return (): void => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [isOpen, anchorRef, dialogRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (dialogRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return (): void =>
      document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, onClose, anchorRef, dialogRef]);

  const baseZIndex = 40;
  const zIndex = baseZIndex + 10 + (depth ?? 0) * 20;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={dialogRef}
          tabIndex={-1}
          {...getDialogAccessibilityProps("", false, ariaLabel)}
          className="fixed w-72 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-1rem)] overflow-y-auto rounded-xl border border-border bg-card shadow-2xl"
          style={{ left: position.left, top: position.top, zIndex }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
          onWheel={stopOverlayWheelPropagation}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
